import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// ── Query key factories ──

export const searchKeys = {
  all: ["search"] as const,
  policies: (q: string) => ["search", "policies", q] as const,
  agencies: (q: string) => ["search", "agencies", q] as const,
  claims: (q: string) => ["search", "claims", q] as const,
  types: ["search", "types"] as const,
} as const;

export const policyKeys = {
  all: ["policies"] as const,
  detail: (policyNumber: string, effectiveDate?: string) =>
    ["policies", "detail", policyNumber, effectiveDate] as const,
  periods: (policyNumber: string) => ["policies", "periods", policyNumber] as const,
  agency: (policyNumber: string) => ["policies", "agency", policyNumber] as const,
  lossRatio: (policyNumber: string, effectiveDate?: string) =>
    ["policies", "loss-ratio", policyNumber, effectiveDate] as const,
  states: (policyNumber: string, effectiveDate?: string) =>
    ["policies", "states", policyNumber, effectiveDate] as const,
  info: (policyNumber: string) => ["policies", "info", policyNumber] as const,
  forms: (policyNumber: string, effectiveDate?: string) =>
    ["policies", "forms", policyNumber, effectiveDate] as const,
  serviceTeam: (policyNumber: string) => ["policies", "service-team", policyNumber] as const,
  contacts: (policyNumber: string) => ["policies", "contacts", policyNumber] as const,
  related: (policyNumber: string) => ["policies", "related", policyNumber] as const,
  locations: (policyNumber: string) => ["policies", "locations", policyNumber] as const,
  claimCounts: (policyNumber: string) => ["policies", "claim-counts", policyNumber] as const,
  mrp: (filters?: { agencyCode?: string; activePolicyBind?: string }) =>
    ["policies", "mrp", filters] as const,
} as const;

export const claimKeys = {
  all: ["claims"] as const,
  detail: (claimNumber: string) => ["claims", "detail", claimNumber] as const,
  financials: (claimNumber: string) => ["claims", "financials", claimNumber] as const,
  policy: (claimNumber: string) => ["claims", "policy", claimNumber] as const,
  agency: (claimNumber: string) => ["claims", "agency", claimNumber] as const,
  mrp: (filters?: Record<string, string | undefined>) => ["claims", "mrp", filters] as const,
  mrpFacets: ["claims", "mrp", "facets"] as const,
  restrictionCheck: (numbers: readonly string[]) =>
    ["claims", "restriction-check", numbers] as const,
} as const;

export const agencyKeys = {
  all: ["agencies"] as const,
  detail: (agentNumber: string) => ["agencies", "detail", agentNumber] as const,
  region: (agentNumber: string) => ["agencies", "region", agentNumber] as const,
  policies: (agentNumber: string) => ["agencies", "policies", agentNumber] as const,
  claims: (agentNumber: string) => ["agencies", "claims", agentNumber] as const,
  contacts: (agentNumber: string) => ["agencies", "contacts", agentNumber] as const,
  scorecardGroups: (group: string) => ["agencies", "scorecard", group] as const,
} as const;

export const teamKeys = {
  all: ["teams"] as const,
  regions: ["teams", "regions"] as const,
  teams: (regionId: string) => ["teams", "teams", regionId] as const,
  users: (teamId: string) => ["teams", "users", teamId] as const,
  history: (filters?: { period?: string; region?: string }) =>
    ["teams", "history", filters] as const,
  historyRegions: (periodId: string | null) => ["teams", "history-regions", periodId] as const,
  historyTeams: (periodId: string | null, region: string | null) =>
    ["teams", "history-teams", periodId, region] as const,
  historyRoster: (periodId: string | null, region: string | null, team: string | null) =>
    ["teams", "history-roster", periodId, region, team] as const,
  periods: ["teams", "periods"] as const,
  report: (filters?: Record<string, string | undefined>) => ["teams", "report", filters] as const,
  unbound: (filters?: Record<string, string | undefined>) => ["teams", "unbound", filters] as const,
  department: (claimRegionId?: string) => ["teams", "department", claimRegionId] as const,
  maintenanceUsers: (search?: string) => ["teams", "maintenance-users", search ?? ""] as const,
} as const;

export const configKeys = {
  all: ["config"] as const,
  cancelReasons: ["config", "cancel-reasons"] as const,
  payPlans: ["config", "pay-plans"] as const,
  regions: ["config", "regions"] as const,
  // Both views project the same singleton Param row. Mutations to either
  // view should invalidate both caches via `paramAll` so the other view
  // refetches when the user navigates to it.
  paramAll: ["config", "param"] as const,
  paramWorkflow: ["config", "param", "workflow"] as const,
  paramUploadPdf: ["config", "param", "upload-pdf"] as const,
  users: (filters?: { search?: string }) => ["config", "users", filters] as const,
} as const;

export const brandingKeys = {
  all: ["branding"] as const,
} as const;

export const userDisplayKeys = {
  all: ["user-display"] as const,
  current: ["user-display", "current"] as const,
} as const;

export const userKeys = {
  all: ["users"] as const,
  list: (filters?: { search?: string }) => ["users", "list", filters] as const,
} as const;

export const announcementKeys = {
  all: ["announcements"] as const,
  // Active list for the current user — drives the banner.
  active: ["announcements", "active"] as const,
  // Admin list — every announcement, regardless of status.
  admin: ["announcements", "admin"] as const,
} as const;

export const demoLoginKeys = {
  all: ["demo-logins"] as const,
} as const;
